import React, { forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlay, faLock, faLockOpen } from "@fortawesome/free-solid-svg-icons";

import { TOASTIFY_OPTIONS } from "../../../config/ReactToastify";
import { useAppStore } from "../../../providers/AppStoreProvider";
import { useAPI } from "../../../providers/ApiProvider";
import { convertSecondsToTime, toastWithAsyncFetch } from "../../../utils/helper";

import "./Lesson.scss";

export const CreateLesson = ({ onClick }) => (
  <div
    role="button"
    className="create-lesson d-flex flex-row align-items-center rounded-10 p-3 mx-3 mt-2 text-black-50"
    onClick={onClick}
  >
    <i className="fa-duotone fa-circle-plus fa-lg me-3" />
    Thêm bài học
  </div>
);

export const Lesson = observer(
  forwardRef(({ lesson, index }, ref) => {
    const store = useAppStore();
    const navigate = useNavigate();
    const api = useAPI();

    let lessonClass =
      "chapter-lesson d-flex flex-row justify-content-between align-items-center px-4 p-3 rounded-10";

    if (store.currentLesson?.id === lesson.id) {
      lessonClass += " " + "viewing";
    }

    const lessonClickHandler = (lessonId) => {
      if (
        !store.hasPermission("update_lesson") &&
        lesson.status === "private" &&
        !store.hasPermission("view_private_lesson")
      ) {
        toast.warn("Vui lòng đăng ký khoá học để xem được bài học này", TOASTIFY_OPTIONS);
        return;
      }

      store.setCurrentLesson(lessonId);
      navigate(`/lesson/${store.course.id}/${lessonId}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toggleLessonStatusHandler = (e) => {
      e.stopPropagation();
      toastWithAsyncFetch(
        {
          loading: "Đang thay đổi trạng thái bài học...",
          success: "Thay đổi trạng thái thành công!",
          error: "Thay dổi trạng thái thất bại!",
        },
        api.callApi.bind(null, "changeLessonStatus", {
          params: { pk: lesson.id },
          body: { status: lesson.status === "private" ? "public" : "private" },
        }),
        () => lesson.toggleStatus(),
      );
    };

    const changeLessonOrderHandler = (e, direction) => {
      e.stopPropagation();

      toastWithAsyncFetch(
        {
          loading: "Đang thay đổi thứ tự bài học...",
          success: "Thay đổi thứ tự thành công!",
          error: "Thay dổi thứ tự thất bại!",
        },
        api.callApi.bind(null, "changeLessonOrder", {
          params: { pk: lesson.id },
          body: { type: direction },
        }),
        () => store.changeLessonOrder(direction, lesson, index),
      );
    };

    return (
      <div
        ref={ref}
        role="button"
        className={lessonClass}
        onClick={lessonClickHandler.bind(null, lesson.id)}
      >
        <div className="d-flex flex-row align-items-start">
          {store.hasPermission("update_lesson") ? (
            <div
              role="button"
              className="lesson-status d-flex align-items-center justify-content-center me-3"
              onClick={toggleLessonStatusHandler}
            >
              <FontAwesomeIcon size="lg" icon={lesson.status === "private" ? faLock : faLockOpen} />
            </div>
          ) : store.hasPermission("view_private_lesson") || lesson.status === "public" ? (
            <div className="lesson-progress me-3" data-done={lesson.isDone}>
              <i className="fa-duotone fa-circle-check fa-xl" />
            </div>
          ) : (
            <div className="lesson-progress me-3">
              <FontAwesomeIcon size="lg" icon={faLock} />
            </div>
          )}

          <div className="d-flex flex-column me-3">
            <div className="name fs-7 lh-sm">{lesson.name}</div>

            {lesson.lesson_type === "video" && (
              <div className="duration d-flex flex-row mt-1 fs-7 text-black-50">
                <div className="lesson-type me-2">
                  <FontAwesomeIcon icon={faCirclePlay} />
                </div>
                <span>
                  {!store.hasPermission("view_private_lesson") ||
                  (lesson.video?.status === "available" && lesson.video?.qualities?.length > 0)
                    ? `${convertSecondsToTime(lesson.duration, "minutes")}  min`
                    : "Processing..."}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="lesson-actions d-flex flex-row">
          {store.hasPermission("update_lesson") && (
            <>
              <div
                className="action d-flex rounded text-black-50"
                disabled={index === 0}
                onClick={(e) => changeLessonOrderHandler(e, "up")}
              >
                <i className="fa-solid px-1 fa-caret-up" />
              </div>
              <div
                className="action d-flex rounded text-black-50 ms-2"
                disabled={index === lesson.chapter.lessons.length - 1}
                onClick={(e) => changeLessonOrderHandler(e, "down")}
              >
                <i className="fa-solid px-1 fa-caret-down" />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }),
);
